import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, Plus, Minus, Wallet, User, DollarSign, History, AlertCircle } from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';

export default function AdminBalanceManagementPage() {
    const { user } = useAuthStore();
    const [tokens, setTokens] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedToken, setSelectedToken] = useState('');
    const [amount, setAmount] = useState('');
    const [type, setType] = useState('add');
    const [reason, setReason] = useState('');
    const [userBalances, setUserBalances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [recentTransactions, setRecentTransactions] = useState([]);

    useEffect(() => {
        fetchTokens();
    }, []);

    useEffect(() => {
        if (selectedUser) {
            fetchUserBalances(selectedUser.id);
        }
    }, [selectedUser]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.length >= 2) {
                searchUsers();
            } else {
                setSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchTokens = async () => {
        try {
            const res = await api.get('/admin/balance/tokens');
            setTokens(res.data.tokens || []);
        } catch (error) {
            console.error('Failed to fetch tokens:', error);
        }
    };

    const searchUsers = async () => {
        setSearchLoading(true);
        try {
            const res = await api.get(`/admin/balance/search-users?search=${searchQuery}`);
            setSearchResults(res.data.users || []);
        } catch (error) {
            console.error('Failed to search users:', error);
        }
        setSearchLoading(false);
    };

    const fetchUserBalances = async (userId) => {
        try {
            const res = await api.get(`/admin/balance/user/${userId}`);
            setUserBalances(res.data.balances || []);
        } catch (error) {
            console.error('Failed to fetch user balances:', error);
        }
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedUser || !selectedToken || !amount || !reason) {
            toast.error('Please fill all fields');
            return;
        }

        if (parseFloat(amount) <= 0) {
            toast.error('Amount must be greater than 0');
            return;
        }

        setLoading(true);
        try {
            const res = await api.post('/admin/balance/modify', {
                userId: selectedUser.id,
                tokenId: selectedToken,
                amount: parseFloat(amount),
                type,
                reason
            });

            toast.success(res.data.message);

            // Refresh user balances
            fetchUserBalances(selectedUser.id);

            // Add to recent transactions
            setRecentTransactions(prev => [{
                ...res.data.data,
                timestamp: new Date().toISOString()
            }, ...prev.slice(0, 9)]);

            // Reset form
            setAmount('');
            setReason('');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to modify balance');
        }
        setLoading(false);
    };

    const getTokenSymbol = (tokenId) => {
        const token = tokens.find(t => t.id === tokenId);
        return token?.symbol || 'Unknown';
    };

    return (
        <div className="min-h-screen bg-[#0b0e11]">
            <AdminNavbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Wallet className="w-8 h-8 text-[#f0b90b]" />
                        User Balance Management
                    </h1>
                    <p className="text-gray-400 mt-2">Add or subtract balance from any user's wallet</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* User Search */}
                        <div className="bg-[#1e2329] rounded-lg p-6 border border-gray-700">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-[#f0b90b]" />
                                Select User
                            </h2>

                            <div className="relative">
                                <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by email or name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#2b3139] text-white pl-10 pr-4 py-3 rounded-lg border border-gray-600 focus:border-[#f0b90b] focus:outline-none"
                                />

                                {/* Search Results Dropdown */}
                                {searchResults.length > 0 && (
                                    <div className="absolute z-10 w-full mt-2 bg-[#2b3139] border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                        {searchResults.map(u => (
                                            <button
                                                key={u.id}
                                                onClick={() => handleUserSelect(u)}
                                                className="w-full px-4 py-3 text-left hover:bg-[#363c45] flex items-center gap-3 border-b border-gray-700 last:border-0"
                                            >
                                                <div className="w-8 h-8 bg-[#f0b90b] rounded-full flex items-center justify-center text-black font-bold">
                                                    {u.email?.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="text-white">{u.firstName} {u.lastName}</p>
                                                    <p className="text-sm text-gray-400">{u.email}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected User Display */}
                            {selectedUser && (
                                <div className="mt-4 p-4 bg-[#2b3139] rounded-lg border border-[#f0b90b]">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#f0b90b] rounded-full flex items-center justify-center text-black font-bold text-lg">
                                                {selectedUser.email?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">{selectedUser.firstName} {selectedUser.lastName}</p>
                                                <p className="text-sm text-gray-400">{selectedUser.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setSelectedUser(null);
                                                setUserBalances([]);
                                            }}
                                            className="text-gray-400 hover:text-white"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Balance Modification Form */}
                        {selectedUser && (
                            <div className="bg-[#1e2329] rounded-lg p-6 border border-gray-700">
                                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-[#f0b90b]" />
                                    Modify Balance
                                </h2>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Type Selection */}
                                    <div className="flex gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setType('add')}
                                            className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${type === 'add'
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-[#2b3139] text-gray-400 hover:bg-[#363c45]'
                                                }`}
                                        >
                                            <Plus className="w-5 h-5" />
                                            Add Balance
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setType('subtract')}
                                            className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${type === 'subtract'
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-[#2b3139] text-gray-400 hover:bg-[#363c45]'
                                                }`}
                                        >
                                            <Minus className="w-5 h-5" />
                                            Subtract Balance
                                        </button>
                                    </div>

                                    {/* Token Selection */}
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-2">Select Token</label>
                                        <select
                                            value={selectedToken}
                                            onChange={(e) => setSelectedToken(e.target.value)}
                                            className="w-full bg-[#2b3139] text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-[#f0b90b] focus:outline-none"
                                        >
                                            <option value="">Choose a token...</option>
                                            {tokens.map(token => (
                                                <option key={token.id} value={token.id}>
                                                    {token.symbol} - {token.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Amount */}
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-2">Amount</label>
                                        <input
                                            type="number"
                                            step="any"
                                            min="0"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder="Enter amount..."
                                            className="w-full bg-[#2b3139] text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-[#f0b90b] focus:outline-none"
                                        />
                                    </div>

                                    {/* Reason */}
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-2">Reason (Required)</label>
                                        <textarea
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder="Enter reason for this transaction..."
                                            rows={3}
                                            className="w-full bg-[#2b3139] text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-[#f0b90b] focus:outline-none resize-none"
                                        />
                                    </div>

                                    {/* Warning */}
                                    <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-yellow-500">
                                            This action will directly modify the user's wallet balance and create a transaction record.
                                            Make sure to enter the correct amount and reason.
                                        </p>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={loading || !selectedToken || !amount || !reason}
                                        className={`w-full py-4 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors ${type === 'add'
                                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                                : 'bg-red-600 hover:bg-red-700 text-white'
                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {loading ? (
                                            'Processing...'
                                        ) : (
                                            <>
                                                {type === 'add' ? <Plus className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                                                {type === 'add' ? 'Add' : 'Subtract'} {amount || '0'} {getTokenSymbol(selectedToken)}
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>

                    {/* Right Column - User Balances & Recent Transactions */}
                    <div className="space-y-6">
                        {/* User Current Balances */}
                        {selectedUser && (
                            <div className="bg-[#1e2329] rounded-lg p-6 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-4">Current Balances</h3>
                                {userBalances.length === 0 ? (
                                    <p className="text-gray-400 text-center py-4">No balances found</p>
                                ) : (
                                    <div className="space-y-3">
                                        {userBalances.map((balance, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-[#2b3139] rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-[#363c45] rounded-full flex items-center justify-center text-xs font-bold text-white">
                                                        {balance.symbol?.charAt(0)}
                                                    </div>
                                                    <span className="text-white font-medium">{balance.symbol}</span>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-white font-medium">{parseFloat(balance.balance).toFixed(4)}</p>
                                                    {balance.lockedBalance > 0 && (
                                                        <p className="text-xs text-gray-400">Locked: {parseFloat(balance.lockedBalance).toFixed(4)}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Recent Transactions */}
                        <div className="bg-[#1e2329] rounded-lg p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <History className="w-5 h-5 text-[#f0b90b]" />
                                Recent Actions
                            </h3>
                            {recentTransactions.length === 0 ? (
                                <p className="text-gray-400 text-center py-8">No recent actions</p>
                            ) : (
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {recentTransactions.map((tx, idx) => (
                                        <div key={idx} className="p-3 bg-[#2b3139] rounded-lg border-l-4 border-l-[#f0b90b]">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`text-sm font-medium ${tx.type === 'add' ? 'text-green-500' : 'text-red-500'}`}>
                                                    {tx.type === 'add' ? '+' : '-'}{tx.amount} {tx.token?.symbol}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {new Date(tx.timestamp).toLocaleTimeString()}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400">{tx.user?.email}</p>
                                            <p className="text-xs text-gray-500 mt-1">{tx.reason}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
